
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

export default function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      icon: 'ri-tools-line',
      title: 'Generator Repair & Maintenance',
      description: 'Expert repair and preventive maintenance services for medium speed diesel and HFO generators to ensure optimal performance and longevity.'
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'Used Generator Sales',
      description: 'High-quality used medium speed diesel and HFO generators from trusted brands, thoroughly inspected and certified for reliable operation.'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Technical Support',
      description: 'Comprehensive technical support and consultation for generator installation, operation, and troubleshooting with 24/7 emergency assistance.'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Parts & Components',
      description: 'Genuine and aftermarket spare parts for all major generator brands, ensuring quick replacement and minimal downtime.'
    },
    {
      icon: 'ri-ship-line',
      title: 'Marine Generator Services',
      description: 'Specialized services for marine diesel generators including HFO systems, fuel treatment, and compliance with maritime regulations.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Emergency Services',
      description: 'Round-the-clock emergency repair services to get your generators back online quickly and minimize operational disruptions.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="services" className="py-20 bg-gray-50" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive generator solutions for industrial and marine applications worldwide
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
              variants={itemVariants}
              whileHover={{ y: -5, transition: { duration: 0.3 } }}
            >
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className={`${service.icon} text-blue-600 text-3xl`}></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">{service.title}</h3>
              <p className="text-gray-600 leading-relaxed">{service.description}</p>
              <motion.button 
                onClick={() => handleServiceClick(service)}
                className="w-full bg-accent-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More
              </motion.button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
